import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import { FeatherLoader, useErrorService } from '@abroad/components'
import { UTCFormatDateShort } from '../../utils/date'
import API from '../../utils/API'
import CONSTANTS from '../../utils/constants'
import ExperienceBadge from './ExperienceBadge'
import ManualPaymentModal from './ManualPaymentModal'

const PAYMENT_FILTER_TABS = [
  { key: CONSTANTS.paymentStatus.SUCCEEDED, label: 'Succeeded' },
  { key: CONSTANTS.paymentStatus.FAILED, label: 'Failed' },
  { key: CONSTANTS.paymentStatus.ALL, label: 'All' },
]

const formatMoney = (value) => {
  if (value == null || value === '') return null
  const n = Number(value)
  if (Number.isNaN(n)) return String(value)
  return `$${n.toLocaleString()}`
}

const normalizePaymentList = (payload) => {
  // admin/payment returns a plain list (legacy shape). Keep wrappers for safety.
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.list)) return payload.list
  if (Array.isArray(payload?.payments)) return payload.payments
  if (Array.isArray(payload?.docs)) return payload.docs
  if (Array.isArray(payload?.results)) return payload.results
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

/** Payment totals live on participants GET `summary` (not admin/payment). */
const normalizeParticipantsPaymentSummary = (summary) => {
  if (!summary || typeof summary !== 'object') return null
  return {
    totalAmount: summary.totalAmount,
    collectedAmount: summary.collectedAmount,
    outstandingAmount: summary.outstandingAmount,
    totalPeople: summary.totalPeople ?? summary.outstandingPeopleCount ?? null,
  }
}

const getPaymentTypeLabel = (paymentType) => {
  if (!paymentType) return '-'
  const typeMap = {
    full: 'Full',
    deposit: 'Deposit',
    remaining: 'Remaining',
    partial: 'Manual',
  }
  return typeMap[paymentType] || paymentType
}

const getPaymentMethodLabel = (paymentMethod) => {
  if (!paymentMethod) return null
  const method = String(paymentMethod).toLowerCase()
  if (method === 'credit_card') return 'Credit card'
  if (method === 'ach' || method === 'wire_transfer') return 'ACH'
  return paymentMethod
}

const isFailedPayment = (payment) =>
  payment?.paid === false && Number(payment?.amount) > 0

const isManualPayment = (payment) => {
  const paymentType = payment?.questPaymentType || payment?.paymentType
  if (paymentType === 'partial') return true
  const stripeId = payment?.stripeCharges?.paymentIntent
  if (!stripeId && (payment?.externalPaymentReference || payment?.adminNotes)) {
    return true
  }
  return false
}

const QuestPayments = ({ questId }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [paymentData, setPaymentData] = useState([])
  const [paymentSummary, setPaymentSummary] = useState(null)
  const [statusFilter, setStatusFilter] = useState(
    CONSTANTS.paymentStatus.SUCCEEDED,
  )
  const [tabCounts, setTabCounts] = useState({
    [CONSTANTS.paymentStatus.SUCCEEDED]: '0',
    [CONSTANTS.paymentStatus.FAILED]: '0',
    [CONSTANTS.paymentStatus.ALL]: '0',
  })
  // Page index is advanced via setPage in the infinite-scroll observer.
  // eslint-disable-next-line no-unused-vars
  const [page, setPage] = useState(1)
  const [showPaymentDetailModal, setShowPaymentDetailModal] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState(null)
  const Error = useErrorService()
  const hasMore = useRef(false)
  const loader = useRef(null)
  const tableWrapRef = useRef(null)
  const observer = useRef(null)
  const limit = 25
  const isNotProduction = process.env.REACT_APP_ENV !== 'prod'
  const payments = Array.isArray(paymentData) ? paymentData : []

  // Show $0 until participants summary arrives — do not use stale questDetail.
  const collectedLabel = useMemo(() => {
    if (paymentSummary?.collectedAmount != null) {
      return formatMoney(paymentSummary.collectedAmount)
    }
    return '$0'
  }, [paymentSummary])

  const outstandingLabel = useMemo(() => {
    if (paymentSummary?.outstandingAmount != null) {
      return formatMoney(paymentSummary.outstandingAmount)
    }
    return '$0'
  }, [paymentSummary])

  const targetTotalLabel = useMemo(() => {
    if (paymentSummary?.totalAmount != null) {
      return formatMoney(paymentSummary.totalAmount)
    }
    return null
  }, [paymentSummary])

  const outstandingPeople = paymentSummary?.totalPeople

  const handleOpenPaymentDetail = (payment) => {
    setSelectedPayment(payment)
    setShowPaymentDetailModal(true)
  }

  const handleClosePaymentDetail = () => {
    setShowPaymentDetailModal(false)
    setSelectedPayment(null)
  }

  // Payment totals come from participants GET summary (admin/payment is list-only).

  useEffect(() => {
    let cancelled = false
    const loadPaymentSummary = async () => {
      if (!questId) return
      try {
        const { data } = await API.AdminAPI.quests.getParticipants(questId)
        if (cancelled) return
        setPaymentSummary(normalizeParticipantsPaymentSummary(data?.summary))
      } catch (e) {
        if (!cancelled) {
          setPaymentSummary(null)
        }
      }
    }
    loadPaymentSummary()
    return () => {
      cancelled = true
    }
  }, [questId])

  // One ALL request for tab badge counts (not 3 separate status GETs).
  useEffect(() => {
    let cancelled = false
    const loadTabCounts = async () => {
      if (!questId) return
      try {
        const { data } = await API.AdminAPI.payment.getUserPaymentInformation({
          limit: 100,
          page: 1,
          filter: {
            type: 'quest',
            targetId: questId,
            status: CONSTANTS.paymentStatus.ALL,
          },
        })
        if (cancelled) return
        const list = normalizePaymentList(data)
        let failed = 0
        list.forEach((payment) => {
          if (isFailedPayment(payment)) failed += 1
        })
        const succeeded = Math.max(0, list.length - failed)
        const allLabel = list.length >= 100 ? '100+' : String(list.length)
        setTabCounts({
          [CONSTANTS.paymentStatus.SUCCEEDED]:
            list.length >= 100 && succeeded >= 100 ? '100+' : String(succeeded),
          [CONSTANTS.paymentStatus.FAILED]: String(failed),
          [CONSTANTS.paymentStatus.ALL]: allLabel,
        })
      } catch (e) {
        if (!cancelled) {
          setTabCounts({
            [CONSTANTS.paymentStatus.SUCCEEDED]: '0',
            [CONSTANTS.paymentStatus.FAILED]: '0',
            [CONSTANTS.paymentStatus.ALL]: '0',
          })
        }
      }
    }
    loadTabCounts()
    return () => {
      cancelled = true
    }
  }, [questId])

  const getPaymentInfo = useCallback(
    async (pageNum, status) => {
      if (pageNum === 1) {
        setPaymentData([])
      }
      setIsLoading(true)
      try {
        const { data } = await API.AdminAPI.payment.getUserPaymentInformation({
          limit,
          page: pageNum,
          filter: {
            type: 'quest',
            targetId: questId,
            status: status || CONSTANTS.paymentStatus.SUCCEEDED,
          },
        })
        const list = normalizePaymentList(data)
        if (list.length === 0 || list.length < limit) {
          hasMore.current = false
        } else {
          hasMore.current = true
        }
        if (pageNum > 1) {
          setPaymentData((prev) => [
            ...(Array.isArray(prev) ? prev : []),
            ...list,
          ])
        } else {
          setPaymentData(list)
          if (!hasMore.current) {
            setTabCounts((prev) => ({
              ...prev,
              [status]: String(list.length),
            }))
          }
        }
        setIsLoading(false)
      } catch (e) {
        Error.showError(e)
        setIsLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questId],
  )

  useEffect(() => {
    setPage(1)
    getPaymentInfo(1, statusFilter)
  }, [getPaymentInfo, statusFilter])

  useEffect(() => {
    if (observer.current) {
      observer.current.disconnect()
    }

    if (!hasMore.current || isLoading || payments.length < limit) {
      return undefined
    }

    let locked = false
    observer.current = new IntersectionObserver(
      (entities) => {
        if (
          !entities[0]?.isIntersecting ||
          !hasMore.current ||
          locked ||
          isLoading
        ) {
          return
        }
        locked = true
        setPage((prev) => {
          const nextPage = prev + 1
          getPaymentInfo(nextPage, statusFilter)
          return nextPage
        })
      },
      {
        // Observe against the table scroll container (not the window) so
        // loading more fires while scrolling inside .experience-tbl-wrap.
        root: tableWrapRef.current,
        rootMargin: '80px',
        threshold: 0,
      },
    )

    if (loader.current) {
      observer.current.observe(loader.current)
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
  }, [getPaymentInfo, isLoading, payments.length, statusFilter])

  const renderPaymentAction = (payment) => {
    const paymentType = payment?.questPaymentType || payment?.paymentType
    const stripeId = payment?.stripeCharges?.paymentIntent
    const stripeMessage = payment?.stripeCharges?.message
    const manual = isManualPayment(payment)

    if (manual || paymentType === 'partial') {
      return (
        <button
          type='button'
          className='experience-linkbtn'
          onClick={() =>
            handleOpenPaymentDetail({
              stripeId,
              stripeMessage,
              paid: payment?.paid,
              amount: payment?.amount,
              paymentType,
              paymentMethod: payment?.paymentMethod,
              externalPaymentReference: payment?.externalPaymentReference,
              adminNotes: payment?.adminNotes,
              fullName: payment?.userId?.fullName,
              email: payment?.userId?.email,
            })
          }>
          View record
        </button>
      )
    }

    if (stripeId) {
      return (
        <a
          href={`${
            isNotProduction
              ? `https://dashboard.stripe.com/test/payments/${stripeId}`
              : `https://dashboard.stripe.com/payments/${stripeId}`
          }`}
          rel='noopener noreferrer'
          className='experience-linkbtn'
          target='_blank'>
          Open in Stripe
        </a>
      )
    }

    return '—'
  }

  const emptyMessage =
    statusFilter === CONSTANTS.paymentStatus.FAILED
      ? 'No failed payments.'
      : statusFilter === CONSTANTS.paymentStatus.SUCCEEDED
      ? 'No succeeded payments.'
      : 'No payments yet.'

  const showEmpty = !isLoading && payments.length === 0

  return (
    <div className='quest-payments experience-manage-list'>
      <div className='experience-sumrow'>
        <div className='experience-stat'>
          <span>Collected</span>
          <b>{collectedLabel}</b>
          <small>
            this experience
            {targetTotalLabel ? ` · of ${targetTotalLabel}` : ''}
          </small>
        </div>
        <div className='experience-stat'>
          <span>Outstanding</span>
          <b>{outstandingLabel}</b>
          <small>
            this experience
            {outstandingPeople != null ? ` · ${outstandingPeople} people` : ''}
          </small>
        </div>
      </div>

      <div className='experience-tabs mb-12px'>
        {PAYMENT_FILTER_TABS.map((tab) => (
          <button
            key={tab.key}
            type='button'
            className={classNames('experience-tab', {
              sel: statusFilter === tab.key,
            })}
            disabled={isLoading}
            onClick={() => setStatusFilter(tab.key)}>
            {tab.label}
            <span className='experience-tab-count'>{tabCounts[tab.key]}</span>
          </button>
        ))}
      </div>

      {showEmpty ? (
        <div className='experience-pay-empty'>{emptyMessage}</div>
      ) : (
        <div className='experience-tbl-wrap' ref={tableWrapRef}>
          <table className='experience-tbl' style={{ minWidth: 720 }}>
            <thead>
              <tr>
                <th>User</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Status</th>
                <th aria-label='Actions' />
              </tr>
            </thead>
            <tbody>
              {isLoading && payments.length === 0 && (
                <tr>
                  <td colSpan='6' className='text-center py-3'>
                    <FeatherLoader />
                  </td>
                </tr>
              )}
              {payments.map((payment, index) => {
                const paymentType =
                  payment?.questPaymentType || payment?.paymentType
                const methodLabel = getPaymentMethodLabel(
                  payment?.paymentMethod,
                )
                const failed = isFailedPayment(payment)
                const manual = isManualPayment(payment)
                const amountLabel = formatMoney(payment?.amount) || '$0'

                return (
                  <tr key={payment?._id || index}>
                    <td className='experience-who'>
                      <b>
                        <Link
                          to={`/admin/users/${payment?.userId?._id}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='experience-who-name word-breakall'>
                          {payment?.userId?.fullName}
                        </Link>
                      </b>
                      <span>
                        <Link
                          to={`/admin/users/${payment?.userId?._id}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='experience-who-email word-breakall'>
                          {payment?.userId?.email}
                        </Link>
                      </span>
                    </td>
                    <td>
                      <b className='text-capitalize'>
                        {getPaymentTypeLabel(
                          paymentType === 'partial'
                            ? payment?.questPaymentType || paymentType
                            : paymentType,
                        )}
                      </b>
                      {methodLabel || manual ? (
                        <span className='experience-pay-method-row'>
                          {methodLabel}
                          {manual ? (
                            <span className='experience-method-tag manual'>
                              Manual
                            </span>
                          ) : null}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <span
                        className={classNames({
                          'experience-pay-failed': failed,
                        })}>
                        {amountLabel}
                      </span>
                      {payment?.promoCode &&
                      payment.promoCode !== 'No Code Applied' ? (
                        <span className='d-block s8 text-muted mt-1'>
                          {payment.promoCode}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {UTCFormatDateShort(payment?.createAt) ||
                        UTCFormatDateShort(payment?.createdAt) ||
                        '—'}
                    </td>
                    <td>
                      {failed ? (
                        <ExperienceBadge title='Failed' variant='red' />
                      ) : (
                        <ExperienceBadge title='Succeeded' variant='green' />
                      )}
                    </td>
                    <td>{renderPaymentAction(payment)}</td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan='6' ref={loader} style={{ padding: 0, border: 0 }}>
                  {isLoading && payments.length > 0 ? (
                    <div className='text-center py-2'>
                      <FeatherLoader />
                    </div>
                  ) : null}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <ManualPaymentModal
        showModal={showPaymentDetailModal}
        handleClose={handleClosePaymentDetail}
        paymentData={selectedPayment}
        readOnly
      />
    </div>
  )
}

export default QuestPayments
